import {ImageIcon} from '@/components/svg/ImageIcon';
import {SendIcon} from '@/components/svg/SendIcon';
import {useCodeUtilisateurEffectif} from '@/hooks/auth/usePermissionsCommerciales';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {AttachmentIcon, CloseIcon} from '@chakra-ui/icons';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Text,
  Textarea,
  Tooltip,
} from '@chakra-ui/react';
import {
  HaliRenderMany,
  HaliRenderOne,
  useCommand,
  useHalState,
} from '@synako/haligator';
import {Navigable} from '@synako/halitrek';
import {ParticipationDesc} from 'api-types/ffconnect-hal';
import {
  DOCUMENTS_ACCEPT,
  IMAGES_ACCEPT,
  useEnvoyerMessageAvecPiecesJointes,
} from 'ff-common';
import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ConversationMessages} from '@/backoffice/domains/CanalCollaboratif/ConversationMessages';
import {MessageListSkeleton} from '@/backoffice/domains/CanalCollaboratif/ConversationsSkeletons';
import {ConversationClientHeader} from './ConversationClientHeader';

interface ConversationClientDetailViewProps {
  participationNav: Navigable<ParticipationDesc>;
  onRetour: () => void;
  onFermer: () => void;
}

/**
 * Vue de détail d'une conversation côté client.
 * Affiche les messages et permet d'en envoyer de nouveaux.
 */
export const ConversationClientDetailView: React.FC<
  ConversationClientDetailViewProps
> = ({participationNav, onRetour, onFermer}) => {
  const {t} = useTranslation();
  const textToast = useSimpleTextToast();
  const onError = useDefaultOnError(textToast);

  // Ref pour le conteneur des messages (pour le scroll)
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Récupération de la participation et de la conversation
  const participationState = useHalState(participationNav);
  const conversationNav = participationNav.follow('conversation');

  // Code utilisateur effectif du viewer : défini pour un commercial (FF/Synako), undefined pour un
  // contact client (get-droits réservé aux FF/Synako). Détermine le côté du viewer dans le fil.
  const currentCodeUtilisateur = useCodeUtilisateurEffectif();

  // Alignement des bulles selon ce côté ("mes" messages à droite, ceux de l'interlocuteur à gauche) :
  // - utilisateur côté client (pas de code_utilisateur) : vue client, les messages contact_client à droite.
  // - utilisateur côté commercial : on aligne par code_utilisateur pour que mes messages soient à droite et les autres à gauche.
  const isClientView = !currentCodeUtilisateur;
  const monType = isClientView ? 'contact_client' : 'utilisateur_ff';

  // Commande pour marquer la participation comme lue
  const {submit: submitMarquerLu} = useCommand(
    participationNav,
    'c:marquer-lu',
    {onError},
  );

  // Hook pour gérer l'envoi de messages avec pièces jointes
  const {
    messageContent,
    setMessageContent,
    selectedFiles,
    isSubmitting,
    peutEnvoyer,
    handleFileSelect,
    handleRemoveFile,
    envoyerMessage,
    documentInputRef,
    imageInputRef,
    textareaRef,
  } = useEnvoyerMessageAvecPiecesJointes(conversationNav, {
    onSuccess: () => {
      // Après envoi de tous les fichiers, marquer comme lu
      submitMarquerLu({});
    },
    onError,
    onFichierTropVolumineux: (nom) => {
      textToast(t('global.fichier_trop_volumineux', {nom}));
    },
  });

  // Commande pour marquer la conversation comme archivée
  const {
    submit: submitTerminerConversation,
    isSubmitting: isSubmittingTerminerConversation,
  } = useCommand(conversationNav, 'c:terminer-conversation', {onError});

  // Édition du titre
  const [isEditingTitre, setIsEditingTitre] = useState(false);
  const [titreEnEdition, setTitreEnEdition] = useState('');
  const {
    submit: submitModifierTitre,
    isSubmitting: isModifyingTitre,
    isAvailable: peutModifierTitre,
  } = useCommand(conversationNav, 'c:modifier-titre', {
    onSuccess: () => {
      setIsEditingTitre(false);
    },
    onError,
  });

  const handleSubmitTitre = () => {
    const titre = titreEnEdition.trim();
    if (titre) {
      submitModifierTitre({titre});
    }
  };

  const handleCancelEditTitre = () => {
    setIsEditingTitre(false);
    setTitreEnEdition('');
  };

  // Marquer automatiquement comme lu quand on ouvre la conversation
  const hasMessagesNonLus =
    (participationState.data?.messages_non_lus ?? 0) > 0;

  useEffect(() => {
    // si il y a des messages non lus, on marque comme lu
    if (hasMessagesNonLus) {
      submitMarquerLu({});
    }
  }, [hasMessagesNonLus, submitMarquerLu]);

  // Envoyer avec Entrée (Shift+Entrée pour nouvelle ligne)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      envoyerMessage();
    }
  };

  // Skeleton de la vue
  const skeleton = (
    <Box
      display='flex'
      flexDirection='column'
      h='100%'
    >
      <ConversationClientHeader
        titre='...'
        onRetour={onRetour}
        onFermer={onFermer}
      />
      <Box
        flex={1}
        p={4}
      >
        <MessageListSkeleton />
      </Box>
    </Box>
  );

  return (
    <HaliRenderOne
      nav={conversationNav}
      skeleton={() => skeleton}
      render={({state: conversationState}) => {
        const conversation = conversationState.data;

        return (
          <Box
            display='flex'
            flexDirection='column'
            h='100%'
          >
            {/* Header (titre éditable si le lien `c:modifier-titre` est présent) */}
            <ConversationClientHeader
              titre={
                conversation.titre ?? t('client.conversations.titre_defaut')
              }
              onRetour={onRetour}
              onFermer={onFermer}
              edition={
                peutModifierTitre
                  ? {
                      isEditing: isEditingTitre,
                      valeur: titreEnEdition,
                      isModifying: isModifyingTitre,
                      onChange: setTitreEnEdition,
                      onStart: (valeurInitiale) => {
                        setTitreEnEdition(valeurInitiale);
                        setIsEditingTitre(true);
                      },
                      onSubmit: handleSubmitTitre,
                      onCancel: handleCancelEditTitre,
                    }
                  : undefined
              }
            />

            <HaliRenderMany
              many={conversationState.follow('messages').followAll('items')}
              skeleton={() => <MessageListSkeleton />}
              render={({states: messageStates}) => {
                // Afficher le bouton "terminer conversation" uniquement si le dernier message
                // ne vient pas de mon propre camp, c'est-à-dire si l'autre interlocuteur a répondu :
                // côté client = le collaborateur FF a répondu, en mode commercial = le client a répondu.
                const dernierMessageEstDeMoi =
                  messageStates[messageStates.length - 1]?.data
                    .expediteur_type === monType;
                const peutAfficherBoutonTerminer =
                  !dernierMessageEstDeMoi && messageStates.length > 0;

                return (
                  <>
                    {/* Zone des messages */}
                    <Box
                      ref={messagesContainerRef}
                      flex={1}
                      overflowY='auto'
                      p={4}
                      bg='gray.bg'
                    >
                      <ConversationMessages
                        messageStates={messageStates}
                        containerRef={messagesContainerRef}
                        emptyMessage={t('client.conversations.aucun_message')}
                        // Vue client : messages du client à droite. Mode commercial : viewer-centric
                        // (mes messages FF à droite via currentCodeUtilisateur).
                        isClientView={isClientView}
                        currentCodeUtilisateur={currentCodeUtilisateur}
                        footerMessage={
                          conversation.statut === 'terminee'
                            ? t(
                                'client.conversations.conversation_cloturee_info',
                              )
                            : undefined
                        }
                      />
                    </Box>

                    {/* Bouton permettant de terminer la conversation */}
                    {peutAfficherBoutonTerminer &&
                      conversation.statut === 'en_cours' && (
                        <Box
                          bg='gray.bg'
                          justifyContent='center'
                          alignItems='center'
                          display='flex'
                          p={1}
                        >
                          <Button
                            isLoading={isSubmittingTerminerConversation}
                            onClick={() => {
                              submitTerminerConversation({});
                            }}
                            fontSize='sm'
                            m={2}
                            bg='white'
                            borderRadius='20px'
                            color='gray.dark'
                            border='1px solid'
                            borderColor='gray.dark'
                            _hover={{bg: 'gray.light'}}
                          >
                            {t('client.conversations.terminer_conversation')}
                          </Button>
                        </Box>
                      )}
                  </>
                );
              }}
            />

            {/* Zone de saisie du message */}
            <Box
              p={4}
              pb={{base: '5rem', md: 4}}
              bg='white'
              borderBottomRadius={{base: 0, md: '12px'}}
              borderTop='1px solid'
              borderColor='gray.light'
            >
              {/* Preview des fichiers sélectionnés (avant envoi) */}
              {selectedFiles.length > 0 && (
                <Box mb={2}>
                  {selectedFiles.map((file, index) => (
                    <HStack
                      key={`${file.name}-${index}`}
                      bg='gray.50'
                      p={2}
                      mb={1}
                      borderRadius='8px'
                      justify='space-between'
                    >
                      <HStack spacing={2}>
                        {file.type.startsWith('image/') ? (
                          <ImageIcon color='gray.dark' />
                        ) : (
                          <AttachmentIcon color='gray.dark' />
                        )}
                        <Text
                          fontSize='sm'
                          color='gray.dark'
                          noOfLines={1}
                        >
                          {file.name}
                        </Text>
                        <Text
                          fontSize='xs'
                          color='gray.500'
                        >
                          (
                          {t('client.conversations.taille_fichier_ko', {
                            taille: (file.size / 1024).toFixed(1),
                          })}
                          )
                        </Text>
                      </HStack>
                      <IconButton
                        aria-label={t('global.supprimer')}
                        icon={<CloseIcon boxSize={3} />}
                        size='xs'
                        variant='ghost'
                        color='gray.dark'
                        onClick={() => {
                          handleRemoveFile(index);
                        }}
                      />
                    </HStack>
                  ))}
                </Box>
              )}
              {/* Sélecteur de fichier documents (déclenché par le bouton trombone) */}
              <input
                type='file'
                ref={documentInputRef}
                style={{display: 'none'}}
                onChange={handleFileSelect}
                accept={DOCUMENTS_ACCEPT}
                multiple // Permet la sélection multiple
              />
              {/* Sélecteur de fichier images (déclenché par le bouton image) */}
              <input
                type='file'
                ref={imageInputRef}
                style={{display: 'none'}}
                onChange={handleFileSelect}
                accept={IMAGES_ACCEPT}
                multiple
              />
              {/* Zone de saisie du message */}
              <Textarea
                placeholder={t('client.conversations.placeholder_message')}
                value={messageContent}
                onChange={(e) => {
                  setMessageContent(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                fontSize='sm'
                bg='transparent'
                border='none'
                resize='none'
                minH='40px'
                maxH='150px'
                height='auto'
                rows={1}
                overflow='auto'
                _focus={{border: 'none', boxShadow: 'none'}}
                ref={textareaRef}
                isDisabled={isSubmitting}
                _disabled={{
                  cursor: 'not-allowed',
                  opacity: 0.6,
                }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = `${target.scrollHeight}px`;
                }}
              />
              {/* Icônes pièce jointe et bouton envoyer */}
              <HStack
                ml={1}
                spacing={1}
                justify='space-between'
              >
                {/* Icônes pièces jointes */}
                <HStack spacing={1}>
                  <Tooltip
                    label={t('client.conversations.ajouter_piece_jointe')}
                  >
                    <IconButton
                      aria-label={t(
                        'client.conversations.ajouter_piece_jointe',
                      )}
                      icon={<AttachmentIcon />}
                      variant='ghost'
                      color='gray.dark'
                      size='sm'
                      onClick={() => {
                        documentInputRef.current?.click();
                      }}
                      isDisabled={isSubmitting}
                    />
                  </Tooltip>
                  <Tooltip label={t('client.conversations.ajouter_image')}>
                    <IconButton
                      aria-label={t('client.conversations.ajouter_image')}
                      icon={<ImageIcon />}
                      variant='ghost'
                      color='gray.dark'
                      size='sm'
                      onClick={() => {
                        imageInputRef.current?.click();
                      }}
                      isDisabled={isSubmitting}
                    />
                  </Tooltip>
                </HStack>
                {/* Bouton envoyer */}
                <IconButton
                  aria-label={t('client.conversations.envoyer')}
                  icon={<SendIcon />}
                  colorScheme='blue'
                  bg='blue.main'
                  color='white'
                  onClick={envoyerMessage}
                  isLoading={isSubmitting}
                  isDisabled={!peutEnvoyer}
                  _hover={{bg: peutEnvoyer ? 'blue.dark' : 'gray.light'}}
                  _disabled={{bg: 'gray.light', cursor: 'not-allowed'}}
                  boxSize='40px'
                  minW='40px'
                  borderRadius='8px'
                />
              </HStack>
            </Box>
          </Box>
        );
      }}
    />
  );
};
